// Chat home page. AppPage shell + chat-specific fetch blocks +
// realtime subscription wiring in onCreate. Sidebar/Main slots are
// filled from components/*.
import { rowToMessage, rowToChannel } from '../../shared/functions/chat/chatUtils.js'
import { appPageBoilerplateFetch } from '../functions/pageBoilerplate.js'
import { ticketColumnsFetch } from '../functions/tickets/ticketUtils.js'

export const home = {
  extends: 'AppPage',
  metadata: { title: 'Chat / Symbols Workspace' },

  onInit: (el, s) => {
    if (el.call('redirectIfExternalOnly', s)) return
  },

  fetch: [
    ticketColumnsFetch,
    {
      method: 'select',
      from: 'workspaceProject.chat',
      order: 'updated_at',
      as: '~/channels',
      cache: '15s',
      transform: (rows) => ({ channels: (rows || []).map(rowToChannel) })
    },
    {
      // migrated 2026-04-27 to workspaceProject.chat.members
      method: 'select',
      from: 'workspaceProject.chat.members',
      as: '~/channelMembers',
      cache: '30s',
      transform: (rows) => ({ channelMembers: rows || [] })
    },
    {
      // Initial load: latest 500 messages across channels for fast first paint.
      // Realtime subscription delivers new messages live; older messages can
      // be lazy-loaded on scroll.
      // migrated 2026-04-27 to workspaceProject.chat.messages
      method: 'select',
      from: 'workspaceProject.chat.messages',
      order: 'created_at',
      ascending: false,
      limit: 500,
      as: '~/messagesByChannel',
      cache: '10s',
      transform: (rows) => {
        const byCh = {}
        for (const r of (rows || [])) {
          const m = rowToMessage(r)
          if (!byCh[m.channelId]) byCh[m.channelId] = []
          byCh[m.channelId].push(m)
        }
        // Reverse each bucket so messages display oldest → newest
        for (const k in byCh) byCh[k].reverse()
        return byCh
      }
    },
    {
      // Mentions of the current user. The chat_mentions table is populated
      // server-side by the chat_extract_mentions trigger on insert.
      // migrated 2026-04-27 to workspaceProject.chat.mentions
      method: 'select',
      from: 'workspaceProject.chat.mentions',
      order: 'created_at',
      as: '~/myMentions',
      cache: '15s',
      transform: (rows) => (rows || [])
    }
  ],

  onCreate: (el, s) => {
    const root = s.root

    // Load pinned channels from localStorage
    el.call('loadPinnedChannels')

    // Hash sync — debounced replaceState so rapid state writes coalesce.
    el._syncHash = () => {
      if (typeof window === 'undefined') return
      clearTimeout(el._hashTimer)
      el._hashTimer = setTimeout(() => {
        const parts = []
        if (root.activeChannelId) parts.push('channel=' + root.activeChannelId)
        if (root.activeTab && root.activeTab !== 'conversation') parts.push('tab=' + root.activeTab)
        if (root.activeThreadRootId) parts.push('thread=' + root.activeThreadRootId)
        const next = parts.join('&')
        if (window.location.hash.slice(1) !== next) {
          history.replaceState(null, '', '#' + next)
        }
      }, 50)
    }

    // Auto-pick: restore from URL hash or fall back to first channel.
    // Uses a polling timer because `onUpdate` on the page element
    // doesn't re-fire when root.channels populates from the fetch.
    let attempts = 0
    el._autoPickTimer = setInterval(() => {
      attempts++
      if (root.activeChannelId || attempts > 50) {
        clearInterval(el._autoPickTimer)
        return
      }
      if (!(root.channels || []).length || !(root.channelMembers || []).length) return

      clearInterval(el._autoPickTimer)

      // Try URL hash first.
      const hash = window.location.hash.slice(1)
      const params = new URLSearchParams(hash)
      const urlChannel = params.get('channel')
      const urlTab = params.get('tab')
      const urlThread = params.get('thread')

      // Restore profile modal from query string (?profile=email)
      const qp = new URLSearchParams(window.location.search)
      const profileParam = qp.get('profile')
      if (profileParam && !root.profileEmail) {
        root.update({ profileEmail: profileParam })
      }

      if (urlChannel) {
        root.update({
          activeChannelId: urlChannel,
          activeTab: urlTab || 'conversation',
          activeThreadRootId: urlThread ? parseInt(urlThread, 10) : null
        })
      } else {
        const me = root.currentUserEmail
        const myIds = new Set(
          root.channelMembers.filter(m => m.member_email === me).map(m => m.channel_id)
        )
        const first = root.channels.find(c => myIds.has(c.id))
        if (first) {
          root.update({ activeChannelId: first.id, activeTab: 'conversation' })
          el.call('markRead', first.id)
        }
      }
      if (el._syncHash) el._syncHash()
    }, 150)

    // Mention-pick: pointerdown fires BEFORE blur so mentionPicker state
    // is still populated. The DOM write runs in setTimeout(0) after DOMQL's
    // reactive flush from the mentionPicker→null state change.
    el._mentionPickHandler = (ev) => {
      const pickEl = ev.target.closest('[data-mention-pick]')
      if (!pickEl) return
      ev.preventDefault()
      ev.stopPropagation()
      const picker = root.mentionPicker
      if (!picker) return
      const value = pickEl.getAttribute('data-mention-pick')
      if (!value) return

      // Determine insertion text based on picker type.
      const type = picker.type || 'person'
      let insert = ''
      if (type === 'person') {
        insert = '@' + value.split('@')[0] + ' '
      } else if (type === 'channel') {
        insert = '#' + value + ' '
      } else if (type === 'ticket') {
        insert = '##' + value + ' '
      } else if (type === 'event') {
        insert = '@@' + value + ' '
      } else if (type === 'reference') {
        insert = '+' + value + ' '
      }

      // Snapshot trigger boundaries before nulling picker.
      const { start, end } = picker
      root.update({ mentionPicker: null })

      // Replace the trigger text in the editor after DOMQL flush.
      setTimeout(() => {
        const editor = document.querySelector('[data-composer-input]')
        if (!editor) return
        const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT)
        let textNode
        let offset = 0
        while ((textNode = walker.nextNode())) {
          const len = textNode.textContent.length
          if (offset + len >= start) {
            const localStart = start - offset
            const localEnd = Math.min(end - offset, len)
            textNode.textContent =
              textNode.textContent.slice(0, localStart) +
              insert +
              textNode.textContent.slice(localEnd)
            // Place caret after inserted text.
            const sel = window.getSelection()
            const r = document.createRange()
            r.setStart(textNode, localStart + insert.length)
            r.collapse(true)
            sel.removeAllRanges()
            sel.addRange(r)
            break
          }
          offset += len
        }
      }, 0)
    }
    document.addEventListener('pointerdown', el._mentionPickHandler, true)

    // Open the realtime subscription.
    el._chatChannel = el.call('subscribeRealtime')

    // ── Test: seed 100 messages for pagination/virtual scroll testing ──
    // Remove this block once testing is complete.
    setTimeout(() => {
      const chId = root.activeChannelId
      if (!chId) return
      const map = { ...(root.messagesByChannel || {}) }
      const existing = map[chId] || []
      if (existing.length >= 100) return
      const testMsgs = []
      const authors = ['nika@symbols.app', 'luka@symbols.app', 'tornike@symbols.app']
      const now = Date.now()
      for (let i = 0; i < 100; i++) {
        const ts = new Date(now - (100 - i) * 60000).toISOString()
        testMsgs.push({
          id: 900000 + i,
          channelId: chId,
          body: `Test message #${i + 1} — this is a seeded message for pagination testing. The quick brown fox jumps over the lazy dog.`,
          authorEmail: authors[i % authors.length],
          createdAt: ts,
          threadRoot: null,
          threadCount: i === 25 ? '3' : '0',
          attachments: i === 42 ? [{ name: 'test-image.png', url: 'https://picsum.photos/400/300', mime: 'image/png', size: 245000 }] : []
        })
      }
      map[chId] = [...testMsgs, ...existing]
      root.update({ messagesByChannel: map })
    }, 2000)

    // Lazy-resolve the WYSIWYG editor node for mention-pick. Can't
    // resolve at onCreate time because Footer mounts later. The getter
    // caches on first successful lookup.
    el._getEditor = () => {
      // Always re-query — the editor can remount across channel switches.
      return document.querySelector('[contenteditable="true"]')
    }

    // Document-level delegated click listener: any element with `data-cid`
    // anywhere in the chat tree becomes the active channel when clicked.
    // This bypasses an apparent v3.14 quirk where onClick on rows generated via
    // `childExtends` + reactive `children` does not get bound at all.
    el._cidClickHandler = (ev) => {
      // Skip if clicking the MoreBtn or its portal — let the dropdown handle it
      if (ev.target.closest('[data-key="MoreBtn"]') || ev.target.closest('[data-sidebar-portal]')) return
      const node = ev.target && ev.target.closest && ev.target.closest('[data-cid]')
      if (!node) return
      const cid = node.getAttribute('data-cid')
      if (!cid) return
      // Switching channels always resets to the Conversation tab —
      // landing on Files/Links/Tickets after a channel switch is jarring.
      root.update({ activeChannelId: cid, activeThreadRootId: null, activeTab: 'conversation' })
      el.call('markRead', cid)
      el.call('markMentionsRead', cid)
      if (el._syncHash) el._syncHash()
    }
    document.addEventListener('click', el._cidClickHandler, true)

    // Same delegation pattern for AppPersonRow → SharedPersonCard. The row
    // tags itself with `data-email`; we look the email up against root.people
    // and write the full person card payload to root.selectedPerson with the
    // row's bounding rect for positioning.
    el._personClickHandler = (ev) => {
      const node = ev.target && ev.target.closest && ev.target.closest('[data-personrow]')
      if (!node) return
      const email = node.getAttribute('data-email')
      if (!email) return
      const person = (root.people || []).find(p => p.email === email)
      if (!person) return
      const rect = node.getBoundingClientRect()
      const CARD_W = 320
      const CARD_H = 280
      const M = 8
      const vw = window.innerWidth
      const vh = window.innerHeight
      let x = rect.right + M
      let y = rect.top
      if (x + CARD_W > vw) x = Math.max(M, rect.left - CARD_W - M)
      if (y + CARD_H > vh) y = Math.max(M, vh - CARD_H - M)
      root.update({
        selectedPerson: {
          name: person.name,
          email: person.email,
          avatar: person.avatar,
          location: person.location,
          timezone: person.timezone,
          x,
          y
        }
      })
    }
    document.addEventListener('click', el._personClickHandler, true)

    // Generic action delegation: every interactive bit in the chat tree
    // tags itself with `data-action`/`data-toggle-group`/etc. and the
    // listeners below resolve them. This avoids the v3.14 binding pitfall and
    // keeps event wiring in one place.
    el._actionClickHandler = (ev) => {
      const t = ev.target
      if (!t || !t.closest) return

      // Action buttons (new-message, new-channel, open-search, close-search…).
      const actionNode = t.closest('[data-action]')
      if (actionNode) {
        const action = actionNode.getAttribute('data-action')
        if (action === 'new-message' || action === 'new-channel' || action === 'new-group') {
          const kind = action === 'new-channel' ? 'channel'
            : action === 'new-group' ? 'group_dm'
            : 'dm'
          root.update({
            showNewChannel: true,
            newChannelDraft: {
              name: '',
              topic: '',
              kind,
              is_private: false,
              members: []
            }
          })
          ev.stopPropagation()
          return
        }
        if (action === 'open-search') {
          root.update({ searchOpen: true, searchQuery: '', searchResults: [] })
          // Focus the search input on the next tick.
          requestAnimationFrame(() => {
            const inp = document.querySelector('[data-search-scrim] input')
            if (inp) inp.focus()
          })
          ev.stopPropagation()
          return
        }
        if (action === 'close-search') {
          root.update({ searchOpen: false, searchQuery: '', searchResults: [] })
          ev.stopPropagation()
          return
        }
        if (action === 'toggle-filter-bar') {
          root.update({ showFilterBar: !root.showFilterBar })
          ev.stopPropagation()
          return
        }
        if (action === 'close-thread') {
          root.update({ activeThreadRootId: null })
          if (el._syncHash) el._syncHash()
          ev.stopPropagation()
          return
        }
        if (action === 'filters-clear') {
          root.update({
            filters: { authorEmail: null, hasAttachments: false, hasMentions: false, query: '' }
          })
          // Reset the uncontrolled search input + author select using the
          // event delegate's actionNode as a stable anchor — no global
          // querySelector required.
          const bar = actionNode.closest && actionNode.closest('[data-filter-bar]')
          if (bar) {
            const inp = bar.querySelector('input')
            if (inp) inp.value = ''
            const sel = bar.querySelector('select')
            if (sel) sel.value = ''
          }
          ev.stopPropagation()
          return
        }
        if (action === 'open-mentions') {
          // Jump to the channel that owns the most recent unread mention.
          const unread = (root.myMentions || []).filter(m => !m.read_at)
          if (!unread.length) return
          const last = unread[unread.length - 1]
          // Find which channel contains this message_id (search loaded buckets).
          let cid = null
          for (const k of Object.keys(root.messagesByChannel || {})) {
            if ((root.messagesByChannel[k] || []).some(m => m.id === last.message_id)) {
              cid = k
              break
            }
          }
          if (cid) {
            root.update({ activeChannelId: cid, activeThreadRootId: null, activeTab: 'conversation' })
            el.call('markRead', cid)
            if (el._syncHash) el._syncHash()
            el.call('markMentionsRead', cid)
          }
          ev.stopPropagation()
          return
        }
      }

      // Sidebar group caret toggles.
      const groupNode = t.closest('[data-toggle-group]')
      if (groupNode) {
        const name = groupNode.getAttribute('data-toggle-group')
        const cur = root.collapsedGroups || {}
        root.update({ collapsedGroups: { ...cur, [name]: !cur[name] } })
        ev.stopPropagation()
        return
      }

      // Tab selection (conversation / files / links / tickets).
      const tabNode = t.closest('[data-tab-select]')
      if (tabNode) {
        const tab = tabNode.getAttribute('data-tab-select')
        if (tab) {
          root.update({ activeTab: tab })
          if (el._syncHash) el._syncHash()
          ev.stopPropagation()
        }
        return
      }

      // Filter toggle pills (hasAttachments, hasMentions).
      const filterNode = t.closest('[data-filter-toggle]')
      if (filterNode) {
        const field = filterNode.getAttribute('data-filter-toggle')
        if (field) {
          root.update({ filters: { ...root.filters, [field]: !(root.filters && root.filters[field]) } })
          ev.stopPropagation()
        }
        return
      }

      // Open thread for a message (from MessageList Reply or ThreadLink).
      const threadNode = t.closest('[data-thread-open]')
      if (threadNode) {
        const id = parseInt(threadNode.getAttribute('data-thread-open'), 10)
        if (!isNaN(id)) {
          root.update({ activeThreadRootId: id })
          if (el._syncHash) el._syncHash()
          ev.stopPropagation()
          return
        }
      }

      // Channel link click — navigate to the referenced channel.
      const channelLink = t.closest('[data-channel-link]')
      if (channelLink) {
        const cid = channelLink.getAttribute('data-channel-link')
        if (cid) {
          root.update({ activeChannelId: cid, activeTab: 'conversation' })
          el.call('markRead', cid)
          if (el._syncHash) el._syncHash()
          ev.stopPropagation()
          return
        }
      }

      // Ticket reference card click — navigate to ticket detail.
      const ticketRef = t.closest('[data-ref-ticket]')
      if (ticketRef) {
        const num = ticketRef.getAttribute('data-ref-ticket')
        if (num) {
          let rootEl = el
          while (rootEl && !rootEl.routes) rootEl = rootEl.parent
          const router = el.context?.utils?.router
          if (router && rootEl)
            router('/tickets?ticket=' + num, rootEl, {}, { pushState: true })
          ev.stopPropagation()
          return
        }
      }

      // Event reference card click — navigate to calendar.
      const eventRef = t.closest('[data-ref-event]')
      if (eventRef) {
        const title = eventRef.getAttribute('data-ref-event')
        if (title) {
          let rootEl = el
          while (rootEl && !rootEl.routes) rootEl = rootEl.parent
          const router = el.context?.utils?.router
          if (router && rootEl)
            router('/calendar', rootEl, {}, { pushState: true })
          ev.stopPropagation()
          return
        }
      }

      // Doc/note reference card click — navigate to documents.
      const docRef = t.closest('[data-ref-doc]')
      if (docRef) {
        const id = docRef.getAttribute('data-ref-doc')
        if (id) {
          let rootEl = el
          while (rootEl && !rootEl.routes) rootEl = rootEl.parent
          const router = el.context?.utils?.router
          if (router && rootEl)
            router('/documents?doc=' + encodeURIComponent(id), rootEl, {}, { pushState: true })
          ev.stopPropagation()
          return
        }
      }

      // Pending attachment chip remove.
      const removeNode = t.closest('[data-attachment-remove]')
      if (removeNode) {
        const aid = removeNode.getAttribute('data-attachment-remove')
        const ch = root.activeChannelId
        if (aid && ch) {
          const next = (root.pendingAttachments[ch] || []).filter(a => a.id !== aid)
          root.update({ pendingAttachments: { ...root.pendingAttachments, [ch]: next } })
          ev.stopPropagation()
          return
        }
      }

      // Search result jump.
      const jumpNode = t.closest('[data-search-jump]')
      if (jumpNode) {
        const msgId = parseInt(jumpNode.getAttribute('data-search-jump'), 10)
        if (!isNaN(msgId)) {
          // Find which channel this message belongs to.
          for (const r of (root.searchResults || [])) {
            if (r.id === msgId) {
              root.update({
                activeChannelId: r.channel_id,
                activeThreadRootId: null,
                activeTab: 'conversation',
                searchOpen: false,
                searchQuery: '',
                searchResults: []
              })
              break
            }
          }
          ev.stopPropagation()
          return
        }
      }

      // Inline link click — open in a new tab.
      const linkNode = t.closest('[data-link]')
      if (linkNode) {
        const url = linkNode.getAttribute('data-link')
        if (url) {
          window.open(url, '_blank', 'noopener')
          ev.preventDefault()
          ev.stopPropagation()
          return
        }
      }

      // Click outside the search overlay → close.
      if (t.matches && t.matches('[data-search-scrim]')) {
        root.update({ searchOpen: false, searchQuery: '', searchResults: [] })
      }
    }
    document.addEventListener('click', el._actionClickHandler, true)

    // Global ⌘K / Ctrl+K opens search.
    el._keyHandler = (ev) => {
      if ((ev.metaKey || ev.ctrlKey) && (ev.key === 'k' || ev.key === 'K')) {
        ev.preventDefault()
        root.update({ searchOpen: true, searchQuery: '', searchResults: [] })
        requestAnimationFrame(() => {
          const inp = document.querySelector('[data-search-scrim] input')
          if (inp) inp.focus()
        })
      }
      if (ev.key === 'Escape' && root.searchOpen) {
        root.update({ searchOpen: false, searchQuery: '', searchResults: [] })
      }
    }
    document.addEventListener('keydown', el._keyHandler, true)
  },

  onRemove: (el) => {
    if (el._chatChannel) {
      try { el._chatChannel.unsubscribe() } catch (_) { /* noop */ }
    }
    if (el._cidClickHandler) {
      document.removeEventListener('click', el._cidClickHandler, true)
    }
    // sidebar-action handler removed — actions handled by ChatSidebarDropdownItem
    if (el._personClickHandler) {
      document.removeEventListener('click', el._personClickHandler, true)
    }
    if (el._actionClickHandler) {
      document.removeEventListener('click', el._actionClickHandler, true)
    }
    if (el._mentionPickHandler) {
      document.removeEventListener('pointerdown', el._mentionPickHandler, true)
    }
    if (el._keyHandler) {
      document.removeEventListener('keydown', el._keyHandler, true)
    }
  },

  ChatSidebar: {},
  ChatMain: {},
  ChatSidebarDropdown: {},
  ChatRenameModal: {},
  ChatNewChannelModal: {},
  ChatChannelSettingsModal: {},
  ChatSearchPanel: {},
  ChatMentionDropdown: {},
  TicketsNewTicketModal: {},
  TicketsDetailModal: {},

  // SharedPersonCard from shared-gov, with chat-specific Actions: a Message
  // button that opens (or creates) a 1:1 DM with the selected person, and a
  // Profile placeholder for parity with calendar's card.
  PersonCard: {
    extends: 'SharedPersonCard',

    Actions: {
      MessageBtn: {
        tag: 'button',
        text: 'Message',
        flex: '1',
        padding: 'Z A',
        round: 'Z',
        border: 'none',
        theme: 'primary',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '600',
        ':hover': { opacity: '.9' },
        onClick: (e, el, s) => {
          const root = s && s.root
          const email = root && root.selectedPerson && root.selectedPerson.email
          if (!email) return
          el.call('messageUser', email)
        }
      },
      ProfileBtn: {
        tag: 'button',
        text: 'Profile',
        flex: '1',
        padding: 'Z A',
        round: 'Z',
        theme: 'secondary',
        borderWidth: '1px',
        borderColor: 'line',
        borderStyle: 'solid',
        cursor: 'pointer',
        fontSize: 'Z2',
        fontWeight: '500',
        ':hover': { background: 'overlay' },
        onClick: (e, el, s) => {
          const root = s && s.root
          const email = root && root.selectedPerson && root.selectedPerson.email
          if (!root) return
          root.update({ selectedPerson: null, profileEmail: email })
        }
      }
    }
  }
}
